// Tiny zero-dependency i18n runtime. Locales live as flat JSON files in
// ./locales/ keyed by dotted paths (e.g. "menu.online"). All bundles are
// imported eagerly at boot so `t()` is sync; bundle size is small (~40 KB
// gzipped for ~40 languages × ~80 strings) so this is fine.
//
// Placeholder syntax: "{name}" — replaced at lookup time.
//   t('hud.round', { round: 3 })  →  "Round 3"
//
// Resolution order: current locale → English fallback → key itself. The
// fallback chain means a missing translation in one locale renders the
// English string rather than a raw key, which keeps shipping safe.

const BUNDLES_RAW = import.meta.glob<Record<string, string>>('./locales/*.json', {
  eager: true,
  import: 'default'
});

const BUNDLES: Record<string, Record<string, string>> = {};
for (const path in BUNDLES_RAW) {
  // path is like './locales/en.json' → 'en'
  const m = path.match(/\/([^/]+)\.json$/);
  if (m) BUNDLES[m[1]!] = BUNDLES_RAW[path]!;
}

const FALLBACK = 'en';
const STORAGE_KEY = 'lang';

let current: string = FALLBACK;

type Vars = Record<string, string | number>;

/** Subscriber list for locale changes. Screens that hold cached
 *  translated strings (HomeScreen labels, in-match HUD pills, the
 *  tutorial NarratorEngine, …) register here so they can re-render on
 *  the spot when the user picks a new language from settings. */
type LocaleListener = (locale: string) => void;
const listeners = new Set<LocaleListener>();

/** Subscribe to locale changes. Returns a detach function that callers
 *  should invoke on screen disposal so disposed screens don't keep
 *  receiving callbacks. */
export function onLocaleChange(cb: LocaleListener): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function setLocale(code: string): void {
  const next = BUNDLES[code] ? code : FALLBACK;
  if (next === current) return;
  current = next;
  try { localStorage.setItem(STORAGE_KEY, current); } catch { /* storage blocked */ }
  // Apply RTL on the document root so CSS direction-aware rules + native
  // controls flip without per-component changes.
  const rtl = RTL_LOCALES.has(current);
  document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
  document.documentElement.setAttribute('lang', current);
  // Fire after the DOM attributes are written so listeners see the
  // canonical state. Each listener catches its own errors so a single
  // misbehaving subscriber can't poison the rest.
  for (const cb of Array.from(listeners)) {
    try { cb(current); } catch (err) {
      console.warn('[i18n] locale-change listener threw', err);
    }
  }
}

export function getLocale(): string { return current; }

export function getStoredLocale(): string | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && BUNDLES[v] ? v : null;
  } catch { return null; }
}

/** Wipe the stored locale. Used by the `?resetLang` debug param so the
 *  language select screen reappears on the next boot — pairs with `?lang`
 *  for forcing a specific pre-selection. Silently no-ops if storage is
 *  blocked. */
export function clearStoredLocale(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* storage blocked */ }
}

/** Best-effort browser-language → supported-locale match. Returns 'en'
 *  if nothing matches. Tries the full tag first ("pt-br"), then the base
 *  ("pt"). */
export function detectBrowserLocale(): string {
  const langs = (navigator.languages?.length ? navigator.languages : [navigator.language || 'en']);
  for (const raw of langs) {
    const tag = raw.toLowerCase();
    if (BUNDLES[tag]) return tag;
    const base = tag.split('-')[0]!;
    if (BUNDLES[base]) return base;
  }
  return FALLBACK;
}

export function t(key: string, vars?: Vars): string {
  const s = BUNDLES[current]?.[key] ?? BUNDLES[FALLBACK]![key] ?? key;
  return vars ? s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? '')) : s;
}

export interface SupportedLanguage {
  code: string;
  /** Native-language label, e.g. "Español" or "日本語". */
  name: string;
  /** Emoji flag — single combined glyph, no leading/trailing whitespace. */
  flag: string;
}

const RTL_LOCALES: ReadonlySet<string> = new Set(['ar', 'he', 'fa']);

/** Display order for the language picker. Each entry must have a matching
 *  JSON file in ./locales/. English first; rest roughly grouped by
 *  region (Europe, MENA, East Asia, South/SE Asia, Africa). */
export const SUPPORTED_LANGUAGES: ReadonlyArray<SupportedLanguage> = [
  { code: 'en',    name: 'English',     flag: '🇬🇧' },
  { code: 'es',    name: 'Español',     flag: '🇪🇸' },
  { code: 'pt-br', name: 'Português (BR)', flag: '🇧🇷' },
  { code: 'pt',    name: 'Português',   flag: '🇵🇹' },
  { code: 'fr',    name: 'Français',    flag: '🇫🇷' },
  { code: 'de',    name: 'Deutsch',     flag: '🇩🇪' },
  { code: 'it',    name: 'Italiano',    flag: '🇮🇹' },
  { code: 'nl',    name: 'Nederlands',  flag: '🇳🇱' },
  { code: 'pl',    name: 'Polski',      flag: '🇵🇱' },
  { code: 'cs',    name: 'Čeština',     flag: '🇨🇿' },
  { code: 'sk',    name: 'Slovenčina',  flag: '🇸🇰' },
  { code: 'hu',    name: 'Magyar',      flag: '🇭🇺' },
  { code: 'ro',    name: 'Română',      flag: '🇷🇴' },
  { code: 'bg',    name: 'Български',   flag: '🇧🇬' },
  { code: 'sr',    name: 'Српски',      flag: '🇷🇸' },
  { code: 'el',    name: 'Ελληνικά',    flag: '🇬🇷' },
  { code: 'sv',    name: 'Svenska',     flag: '🇸🇪' },
  { code: 'no',    name: 'Norsk',       flag: '🇳🇴' },
  { code: 'da',    name: 'Dansk',       flag: '🇩🇰' },
  { code: 'fi',    name: 'Suomi',       flag: '🇫🇮' },
  { code: 'ru',    name: 'Русский',     flag: '🇷🇺' },
  { code: 'uk',    name: 'Українська',  flag: '🇺🇦' },
  { code: 'tr',    name: 'Türkçe',      flag: '🇹🇷' },
  { code: 'ar',    name: 'العربية',     flag: '🇸🇦' },
  { code: 'he',    name: 'עברית',       flag: '🇮🇱' },
  { code: 'fa',    name: 'فارسی',       flag: '🇮🇷' },
  { code: 'ja',    name: '日本語',       flag: '🇯🇵' },
  { code: 'ko',    name: '한국어',       flag: '🇰🇷' },
  { code: 'zh-cn', name: '简体中文',     flag: '🇨🇳' },
  { code: 'zh-tw', name: '繁體中文',     flag: '🇹🇼' },
  { code: 'hi',    name: 'हिन्दी',        flag: '🇮🇳' },
  { code: 'bn',    name: 'বাংলা',         flag: '🇧🇩' },
  { code: 'ta',    name: 'தமிழ்',        flag: '🇮🇳' },
  { code: 'id',    name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'ms',    name: 'Bahasa Melayu', flag: '🇲🇾' },
  { code: 'th',    name: 'ไทย',          flag: '🇹🇭' },
  { code: 'vi',    name: 'Tiếng Việt',   flag: '🇻🇳' },
  { code: 'fil',   name: 'Filipino',     flag: '🇵🇭' },
  { code: 'sw',    name: 'Kiswahili',    flag: '🇰🇪' },
  { code: 'af',    name: 'Afrikaans',    flag: '🇿🇦' }
];
